import { t } from "i18next";
import { ChangeEvent, FC, useContext } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";

import TextInput from "@app/components/inputs/Text";
import Toggle from "@app/components/inputs/Toggle";
import { SalesChannelErrors } from "@app/types/Common.types";
import { SalesChannelFormData } from "@app/types/SalesChannel.types";
import { DISABLED_SALES_CHANNELS, SALES_CHANNEL_FORM_VALIDATION } from "@app/utils/constant";
import { AppContext } from "@app/utils/contexts/App.context";
import { FormContext } from "@app/utils/contexts/Form.context";

import "./SalesChannel.form.scss";

interface SalesChannelFormProps {
    data: SalesChannelFormData;
    onChange: (_name: string, _value: string | number | boolean) => void;
    errors?: Partial<SalesChannelErrors>;
    isEdit?: boolean;
}

const SalesChannelForm: FC<SalesChannelFormProps> = ({ data, onChange, errors, isEdit }) => {
    const { setIsDirty } = useContext(FormContext);
    const { userPermissions } = useContext(AppContext);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsDirty(true);
        const target = e?.target;
        if (!target) return;
        const { name, value } = target;
        onChange?.(name, value);
    };

    const showStatusToggle = isEdit && !DISABLED_SALES_CHANNELS.includes(data?.name ?? "");

    const disabledBackupSalesChannel =
        isEdit &&
        DISABLED_SALES_CHANNELS.includes(data?.name ?? "") &&
        DISABLED_SALES_CHANNELS.includes(data?.posName ?? "");

    return (
        <Container fluid className='py-4 px-5'>
            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        label={t("ADD.SALESCHANNEL.DISPLAY_NAME.LABEL") as string}
                        placeholder={t("ADD.SALESCHANNEL.DISPLAY_NAME.PLACEHOLDER") as string}
                        value={data?.name}
                        onChange={handleChange}
                        name='name'
                        error={errors?.name}
                        disabled={disabledBackupSalesChannel}
                    />
                </Col>
                <Col xs={6}>
                    <TextInput
                        label={t("ADD.SALESCHANNEL.POS_NAME.LABEL") as string}
                        placeholder={t("ADD.SALESCHANNEL.POS_NAME.PLACEHOLDER") as string}
                        value={data?.posName}
                        onChange={handleChange}
                        name='posName'
                        error={errors?.posName}
                        disabled={!!isEdit}
                    />
                    <Form.Text className='text-input__error text-error-red'>
                        {t("ADD.SALESCHANNEL.POS_NAME_MISMATCH_NOTE")}
                    </Form.Text>
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        label={t("ADD.SALESCHANNEL.SGT.LABEL") as string}
                        placeholder={t("ADD.SALESCHANNEL.SGT.PLACEHOLDER") as string}
                        value={data?.weightage ? String(data?.weightage) : ""}
                        onChange={handleChange}
                        name='weightage'
                        error={errors?.weightage}
                        type='number'
                        min={String(SALES_CHANNEL_FORM_VALIDATION.MIN_WEIGHTAGE)}
                        max={String(SALES_CHANNEL_FORM_VALIDATION.MAX_WEIGHTAGE)}
                    />
                    <Form.Text className='text-default-note'>
                        {t("ADD.SALESCHANNEL.DEFAULT_WEIGHTAGE_NOTE")}
                    </Form.Text>
                </Col>
            </Row>
            {showStatusToggle && (
                <Row className='mt-4'>
                    <Col>
                        <Form.Group>
                            <Form.Label className='text-input__label'>Status</Form.Label>
                            <Toggle
                                isActive={!!data?.status}
                                isDisabled={!userPermissions?.["Deactivate_Sales Channel"]}
                                onToggle={() => {
                                    setIsDirty(true);
                                    onChange("status", !(data?.status ?? false));
                                }}
                                display={
                                    data?.status
                                        ? (t("COMMON.ACTIVE") as string)
                                        : (t("COMMON.INACTIVE") as string)
                                }
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default SalesChannelForm;
